// PrivacyPolicyPage.jsx
import React from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Users, FileText } from "lucide-react";

const headerVariants = {
  hidden: { opacity: 0, y: -40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const PrivacyPolicyPage = () => {
  return (
    <motion.div
      className="min-h-screen w-full bg-gradient-to-br from-teal-50 via-green-50 to-white text-gray-800"
      initial="hidden"
      animate="visible"
    >
      <main className="pt-10 pb-24 px-4 sm:px-6 lg:px-8">
        {/* HERO */}
        <header className="text-center mb-16">
          <motion.div variants={headerVariants} className="max-w-4xl mx-auto">
            {/* <Shield className="w-16 h-16 text-teal-600 mx-auto mb-4" /> */}
<h1 className="text-5xl md:text-6xl font-extrabold leading-tight pb-3 bg-clip-text text-transparent bg-gradient-to-r from-teal-700 to-green-600">
  Privacy Policy
</h1>


            <p className="text-xl text-gray-700 mt-4 max-w-3xl mx-auto">
              Your trust matters to us. This policy explains how TalkyToon
              collects, uses, and protects information.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </motion.div>
        </header>

        {/* CONTENT */}
        <motion.section
          variants={sectionVariants}
          className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl p-6 md:p-12 border"
        >
          <div className="prose prose-lg max-w-none">
            <h2 className="flex items-center gap-3 text-teal-700">
              <Lock /> Introduction
            </h2>
            <p>
              TalkyToon is an online learning platform designed for children,
              parents, and educators. We are committed to protecting user
              privacy, especially the privacy of children.
            </p>
            <p>
              This Privacy Policy explains what information we collect, how we
              use it, and the steps we take to keep it safe.
            </p>

            <h2 className="flex items-center gap-3 text-teal-700 mt-10">
              <Users /> Information We Collect (Parents & Guardians)
            </h2>
            <p>
              We collect limited personal information from parents or legal
              guardians only when necessary to provide our services.
            </p>
            <ul>
              <li>Name and email address</li>
              <li>Communication messages sent to our support team</li>
              <li>
                Payment-related information (processed securely by third-party
                payment providers; we do not store card details)
              </li>
            </ul>

            <h2 className="flex items-center gap-3 text-teal-700 mt-10">
              <Lock /> Information About Children
            </h2>
            <p>
              TalkyToon is designed to be child-friendly and privacy-focused. We
              do not knowingly collect personal information from children under
              the age of 13.
            </p>
            <ul>
              <li>
                Anonymous usage data such as learning progress or activity
                completion
              </li>
              <li>
                Technical data required for app functionality (for example,
                device type or performance logs)
              </li>
            </ul>
            <p>
              This data is used only to improve learning experiences and cannot
              be used to identify an individual child.
            </p>

            <h2 className="flex items-center gap-3 text-teal-700 mt-10">
              <FileText /> Data Protection
            </h2>
            <p>
              We use reasonable security measures to protect your information,
              including secure databases and restricted access. However, no
              method of transmission over the internet is 100% secure.
            </p>

            <h2 className="flex items-center gap-3 text-teal-700 mt-10">
              <FileText /> Contact Us
            </h2>
            <p>
              If you have questions or concerns about this Privacy Policy, please
              contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:talkytoonofficial@gmail.com"
                className="text-indigo-600 font-semibold"
              >
                talkytoonofficial@gmail.com
              </a>
              <p className="text-sm text-gray-500 mt-2">
                TalkyToon operates as an online platform and does not have a
                public physical office.
              </p>
            </div>
          </div>
        </motion.section>
      </main>
    </motion.div>
  );
};

export default PrivacyPolicyPage;
